import { Select, Button1, TagInput } from "../index";
import copyIcon from "../../assets/icons/copy-07.png";
import successIcon from "../../assets/icons/success-icon.png";
import { Input, message } from "antd";
import { useState, useEffect } from "react";
import { accountsApi } from "../../api/endpoints";
import CurrencyFlag from "../currency-flag/CurrencyFlag";

const CATEGORIES = ["Top-up", "Salary", "Refund", "Other"];

const BankingPortal = ({
  accounts = [],
  targetAccountId,
  setTargetAccountId,
  targetAccount,
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [instructions, setInstructions] = useState(null);

  // Deposit instructions come from the backend (env-driven float account +
  // this wallet's account number as the payment reference). No hardcoding.
  useEffect(() => {
    if (!targetAccountId) {
      setInstructions(null);
      return;
    }
    let cancelled = false;
    accountsApi
      .depositInstructions(targetAccountId)
      .then((data) => !cancelled && setInstructions(data))
      .catch(() => !cancelled && setInstructions(null));
    return () => {
      cancelled = true;
    };
  }, [targetAccountId]);

  const accountOptions = accounts.map((a) => ({
    name: `${a.currency} ****${(a.accountNumber || "0000").slice(
      -4
    )}${a.isDefault ? " (Default)" : ""}`,
    value: a.id,
  }));

  // Real deposit model: funds are sent to ARM Financial's receiving account,
  // and the user's wallet account number is the mandatory payment reference
  // so we know which wallet to credit.
  const details = [
    {
      name: "Account Name",
      detail: instructions?.accountName || "-",
      copy: true,
    },
    {
      name: "BSB",
      detail: instructions?.bsb || "-",
      copy: true,
    },
    {
      name: "Account Number",
      detail: instructions?.accountNumber || "-",
      copy: true,
    },
    {
      name: "Payment Reference (required)",
      detail: instructions?.reference || targetAccount?.accountNumber || "-",
      copy: true,
      highlight: true,
    },
    {
      name: "Account Location",
      detail: instructions?.location || "Australia",
      copy: false,
    },
  ];

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
    message.success("Copied");
  };

  const handleSubmit = async () => {
    setError(null);
    if (!targetAccountId) {
      setError("Please select an account");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      const res = await accountsApi.topup(targetAccountId, Number(amount));
      message.success(
        res?.message ||
          `Deposit pending. Ref: ${res?.reference}. Will credit in 1 hour.`
      );
      setAmount("");
      setReference("");
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Top up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="col-lg-5 mb-5  p-3">
        <span className="fs-5 fw-bold">Add Funds Via Banking Portal</span>
        <div className="d-flex flex-column mt-4 gap-2">
          <span className="fs14px">Top up to account</span>
          <Select
            value={targetAccountId}
            options={accountOptions}
            defaultValue="Select your account"
            className="rounded-2 p-3"
            onChange={(e) => setTargetAccountId(e.target.value)}
          />

          <div className="d-flex flex-column gap-3 border rounded-2 p-3">
            <div className="d-flex align-items-center gap-2 ">
              <CurrencyFlag currency={targetAccount?.currency} size={28} />
              <span className=" fw-semibold">
                {targetAccount?.currency || "AUD"} deposit details
              </span>
            </div>

            {details.map((detail) => (
              <div
                key={detail.name}
                className="d-flex justify-content-between align-items-center"
              >
                <div className="d-flex flex-column">
                  <span className="fs14px" style={{ color: "#636363" }}>
                    {detail.name}
                  </span>
                  <span
                    className="fs14px fw-bold"
                    style={detail.highlight ? { color: "var(--accent-color)" } : undefined}
                  >
                    {detail.detail}
                  </span>
                </div>
                {detail.copy && (
                  <div
                    className="border rounded-circle p-2 border-success cursor-pointer d-flex"
                    onClick={() => handleCopy(detail.detail)}
                  >
                    <img src={copyIcon} alt="copy-icon" width={20} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="d-flex flex-column gap-1">
            <div>
              <label className="fs12px fw-semibold">Amount</label>
              <Input
                placeholder="0.00"
                className="p-3"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="mt-2">
              <label className="fs12px fw-semibold">
                Transaction reference
              </label>
              <Input
                placeholder="Transaction reference"
                className="p-3"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>

            <span
              className="fs12px fw-medium pe-3"
              style={{ color: "#7D7D7D" }}
            >
              Please ensure that you add transaction reference, otherwise funds
              may reach in your account late
            </span>
          </div>

          <div
            className="d-flex rounded-4 p-3 mt-2 gap-2 align-items-center"
            style={{ backgroundColor: "#F7F7F7" }}
          >
            <img
              src={successIcon}
              alt="success-icon"
              width={44}
              className="align-self-start"
            />

            <div className="d-flex flex-column gap-1">
              <span className="fw-medium">
                Local bank transfer are accepted
              </span>
              <span
                className="fs12px fw-medium pe-3"
                style={{ color: "#4A4A4A" }}
              >
                Funds added via local transfer have no fees and should appear
                within one business day.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-lg-6 mb-5  p-3">
        <span className="fs-5 fw-bold">Additional Details</span>
        <div className="d-flex flex-column gap-2 mt-4">
          <label className="fs12px ">Category</label>
          <Select
            value={category}
            options={CATEGORIES.map((c) => ({ name: c, value: c }))}
            defaultValue="Choose Category"
            className="rounded-2 p-3"
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>
        <div className="d-flex flex-column gap-2 mt-4">
          <label className="fs12px ">Tags</label>
          <TagInput
            className="border py-2"
            inputBgColor="transparent"
            tagColor="#1bbb6b"
            tagTextColor="#000"
            limit={10}
            placeholder="Add up to 10 Tags"
          />
        </div>
      </div>

      {error && (
        <div className="col-12 p-2">
          <span className="fs12px" style={{ color: "#FC555B" }}>
            {error}
          </span>
        </div>
      )}

      <div className="d-flex justify-content-end p-2">
        <Button1
          buttonName={submitting ? "Processing..." : "Submit"}
          className="btn-dark align-self-end"
          onClick={handleSubmit}
          disabled={submitting}
        />
      </div>
    </>
  );
};

export default BankingPortal;
